import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChatCircle, PaperPlaneRight, Plus } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

export default function Messages() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ to_role: "parent", subject: "", body: "" });

  const load = () => api.get("/messages").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.subject || !form.body) return toast.error("Subject and message required");
    try {
      await api.post("/messages", form); toast.success("Message sent");
      setOpen(false); setForm({ to_role: "parent", subject: "", body: "" }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">{t("nav.messages")}</div>
          <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: "Manrope" }}>{t("messages.title")}</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-message-btn" className="rounded-full gap-2"><Plus size={16} weight="bold" /> Compose</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Compose Message</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs uppercase tracking-wider font-semibold">To (role)</Label>
                <Select value={form.to_role} onValueChange={(v) => setForm({ ...form, to_role: v })}>
                  <SelectTrigger className="mt-1.5 h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["parent", "student", "teacher", "secretary", "branch_manager", "director"].map((r) =>
                      <SelectItem key={r} value={r} className="capitalize">{r.replace("_", " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Subject</Label>
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5 h-10" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Message</Label>
                <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={5} className="mt-1.5" /></div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="message-send" className="gap-2"><PaperPlaneRight size={14} /> Send</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-border/60 lg:col-span-1"><CardContent className="p-0">
          <ul className="divide-y divide-border max-h-[600px] overflow-y-auto scrollbar-thin">
            {rows.length === 0 && <li className="p-6 text-sm text-muted-foreground text-center">No messages</li>}
            {rows.map((m) => (
              <li key={m.id}>
                <button onClick={() => setSelected(m)} data-testid={`msg-${m.id}`}
                  className={cn("w-full text-left p-4 hover:bg-secondary/40 transition-colors",
                    selected?.id === m.id && "bg-secondary/60")}>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm shrink-0">
                      {m.from_name?.charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-sm truncate">{m.from_name}</div>
                        {!m.read && <span className="w-2 h-2 rounded-full bg-destructive shrink-0" />}
                      </div>
                      <div className="text-xs font-semibold truncate">{m.subject}</div>
                      <div className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">{m.body}</div>
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </CardContent></Card>

        <Card className="border-border/60 lg:col-span-2 min-h-[400px]"><CardContent className="p-6">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-muted-foreground py-16">
              <ChatCircle size={40} weight="duotone" />
              <p className="mt-3 text-sm">Select a message to read</p>
            </div>
          ) : (
            <div>
              <div className="flex items-start justify-between border-b border-border pb-4">
                <div>
                  <h2 className="text-xl font-bold" style={{ fontFamily: "Manrope" }}>{selected.subject}</h2>
                  <div className="text-xs text-muted-foreground mt-1">
                    From <span className="font-semibold text-foreground">{selected.from_name}</span> · <span className="capitalize">{selected.from_role.replace("_", " ")}</span> · {new Date(selected.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
              <p className="text-sm mt-4 whitespace-pre-wrap">{selected.body}</p>
            </div>
          )}
        </CardContent></Card>
      </div>
    </div>
  );
}
